import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, FileUp, Mail, ArrowRight, Check, X, FileText, User } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import SiteFooter from "@/components/SiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Join() {
  const { user, loading } = useAuth();
  const [submitted, setSubmitted] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground text-xs uppercase tracking-[0.3em]">
        LOADING<span className="blink-caret" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-foreground/30 glass-strong sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            {user ? (
              <Link to="/dashboard" data-testid="join-dashboard-link" className="btn-ghost">Dashboard</Link>
            ) : (
              <Link to="/auth" data-testid="join-login-link" className="btn-ghost">Login</Link>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-5 md:px-10 py-16 md:py-24 w-full" data-testid="join-page">
        <div className="terminal-label mb-6 text-primary">[ HELP.WANTED ]</div>

        <h1 className="serif-mod text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1.05] mb-6" style={{ fontWeight: 500 }}>
          Sisyphean is looking for <span className="whimsy text-primary text-[1.1em]">help</span>.
        </h1>

        <p className="text-sm sm:text-base text-foreground/75 leading-relaxed max-w-2xl mb-10">
          Sisyphean is a small, ambitious project built by one person. If you're a designer, engineer, writer, recruiter, hiring-side operator, or someone who loves creative work and wants to make something real with us, we'd love to hear from you. There's no formal job posting. Tell us how you'd like to help and what you bring.
        </p>

        {submitted ? (
          <ThanksPanel />
        ) : user ? (
          <LoggedInForm user={user} onDone={() => setSubmitted(true)} />
        ) : (
          <AnonymousForm onDone={() => setSubmitted(true)} />
        )}
      </main>

      <SiteFooter variant="site" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Success state
// ---------------------------------------------------------------------------
function ThanksPanel() {
  return (
    <div className="brut-tile p-8 sm:p-10 text-center" style={{ borderColor: "hsl(var(--accent-green))" }} data-testid="join-thanks">
      <div className="inline-flex items-center justify-center w-12 h-12 mb-5 border-2 border-accent-green text-accent-green">
        <Check className="w-6 h-6" strokeWidth={2} />
      </div>
      <h2 className="serif-mod text-2xl tracking-tight mb-3" style={{ fontWeight: 500 }}>Got it.</h2>
      <p className="text-sm text-foreground/75 max-w-md mx-auto">
        Your note is on its way to <span className="font-mono">hello@sisyphean.ai</span>. If it's a good fit we'll reach out within a week or two. Thank you for caring.
      </p>
      <Link to="/" data-testid="join-back-home" className="btn-secondary inline-flex items-center gap-2 mt-8">
        Back home <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mailto card (always shown)
// ---------------------------------------------------------------------------
function MailtoCard() {
  return (
    <div className="brut-tile p-5 sm:p-6 mb-3 flex items-start gap-4" data-testid="join-mailto-card">
      <Mail className="w-5 h-5 text-foreground/70 mt-1 flex-shrink-0" strokeWidth={1.5} />
      <div className="min-w-0 flex-1">
        <div className="terminal-label mb-1">Prefer email</div>
        <p className="text-sm text-foreground/75 leading-relaxed">
          Drop a note to <a href="mailto:hello@sisyphean.ai" className="text-primary underline underline-offset-4" data-testid="join-mailto-link">hello@sisyphean.ai</a> explaining how you'd like to help. No template, just write.
        </p>
      </div>
    </div>
  );
}

function OrDivider({ label = "or use this form" }) {
  return (
    <div className="text-center my-8 text-[10px] tracking-[0.3em] uppercase text-foreground/40 font-mono">
      <span className="px-3 bg-background">{label}</span>
      <div className="border-t border-foreground/15 -mt-2 -z-10" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Logged-in form: send from profile (auto-generates PDF snapshot)
// ---------------------------------------------------------------------------
function LoggedInForm({ user, onDone }) {
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 20) {
      toast.error("Tell us a little more about how you'd like to help. At least a sentence or two.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contribute/from-profile", { message: message.trim() });
      toast.success("Sent. We'll see your profile + pitch.");
      onDone();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please try emailing hello@sisyphean.ai directly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <MailtoCard />
      <OrDivider label="or send from your profile" />

      <div className="brut-tile p-5 sm:p-6 mb-6 flex items-start gap-4 border-primary" style={{ borderColor: "hsl(var(--primary))" }} data-testid="join-from-profile-card">
        <FileText className="w-5 h-5 text-primary mt-1 flex-shrink-0" strokeWidth={1.5} />
        <div className="min-w-0 flex-1">
          <div className="terminal-label mb-1 text-primary">Send my profile</div>
          <p className="text-sm text-foreground/75 leading-relaxed">
            You're signed in as <span className="font-mono text-foreground">{user.email}</span>. We'll auto-generate a PDF snapshot of your profile (contact info, work history, portfolio pieces, writing samples) and attach it to your note. No re-uploading.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="join-from-profile-form" noValidate>
        <Field label="How can you help?" required hint="At least a sentence or two. The more specific, the better.">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={5000}
            rows={6}
            placeholder="I've shipped 4 SaaS marketing sites and 2 brand systems. I'd love to help with the marketing site, illustration system, or product UX. I have ~5 hrs/week."
            className="input-base resize-y leading-relaxed"
            data-testid="join-fp-message-input"
          />
          <div className="flex justify-between items-center mt-1 text-[10px] tracking-[0.2em] uppercase font-mono">
            <span className={message.trim().length < 20 ? "text-amber-500" : "text-foreground/45"}>
              {message.trim().length < 20 ? `${20 - message.trim().length} more character${20 - message.trim().length === 1 ? '' : 's'} needed` : "Looks good"}
            </span>
            <span className="text-foreground/45">{message.length} / 5000</span>
          </div>
        </Field>

        <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-5">
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary inline-flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="join-fp-submit-btn"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <>Send from my profile <ArrowRight className="w-4 h-4" /></>}
          </button>
          <p className="text-[10px] tracking-[0.18em] uppercase text-foreground/45 font-mono">
            Includes a PDF of everything in your account
          </p>
        </div>
      </form>

      <p className="text-[11px] text-foreground/45 mt-8 font-mono leading-relaxed">
        Want to update your profile first? <Link to="/profile" className="text-primary underline underline-offset-4">/profile</Link>
        &nbsp;·&nbsp;
        Haven't built one yet? <Link to="/onboarding" className="text-primary underline underline-offset-4">/onboarding</Link>
      </p>
    </>
  );
}

// ---------------------------------------------------------------------------
// Anonymous form (existing flow)
// ---------------------------------------------------------------------------
function AnonymousForm({ onDone }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Resume too large (max 10MB).");
      return;
    }
    const okExt = ["pdf", "docx", "doc", "txt", "rtf"];
    const ext = (f.name || "").split(".").pop().toLowerCase();
    if (!okExt.includes(ext)) {
      toast.error("Only PDF, DOCX, DOC, TXT, or RTF allowed.");
      return;
    }
    setResume(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    const emailClean = email.trim();
    if (!emailClean || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailClean)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (message.trim().length < 20) {
      toast.error("Tell us a little more about how you'd like to help. At least a sentence or two.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("email", emailClean);
      fd.append("message", message.trim());
      if (resume) fd.append("resume", resume);
      await axios.post(`${API}/contribute`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      toast.success("Got it. We'll be in touch.");
      onDone();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please try emailing hello@sisyphean.ai directly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <MailtoCard />
      <OrDivider />

      <form onSubmit={submit} className="space-y-6" data-testid="join-form" noValidate>
        <Field label="Your name" optional>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            placeholder="Alex Rivera"
            className="input-base"
            data-testid="join-name-input"
            autoComplete="name"
          />
        </Field>

        <Field label="Email" required>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={200}
            placeholder="you@something.com"
            className="input-base"
            data-testid="join-email-input"
            autoComplete="email"
          />
        </Field>

        <Field label="How can you help?" required hint="At least a sentence or two. The more specific, the better.">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={5000}
            rows={6}
            placeholder="I'm a brand designer who's worked on early-stage SaaS launches. I'd love to help with the marketing site, illustration system, or product UX. I have ~5 hrs/week."
            className="input-base resize-y leading-relaxed"
            data-testid="join-message-input"
          />
          <div className="flex justify-between items-center mt-1 text-[10px] tracking-[0.2em] uppercase font-mono">
            <span className={message.trim().length < 20 ? "text-amber-500" : "text-foreground/45"}>
              {message.trim().length < 20 ? `${20 - message.trim().length} more character${20 - message.trim().length === 1 ? '' : 's'} needed` : "Looks good"}
            </span>
            <span className="text-foreground/45">{message.length} / 5000</span>
          </div>
        </Field>

        <Field label="Resume or portfolio" optional hint="PDF, DOCX, DOC, TXT, or RTF. Max 10MB.">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.doc,.txt,.rtf"
            onChange={onFile}
            className="hidden"
            data-testid="join-resume-input"
          />
          {resume ? (
            <div className="flex items-center justify-between gap-3 p-3 border border-foreground/20 bg-background/40 font-mono text-xs" data-testid="join-resume-chip">
              <span className="truncate flex items-center gap-2">
                <FileUp className="w-3 h-3 text-accent-green flex-shrink-0" strokeWidth={2} />
                {resume.name} <span className="text-foreground/45">({Math.round(resume.size / 1024)}KB)</span>
              </span>
              <button
                type="button"
                onClick={() => { setResume(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                className="text-foreground/55 hover:text-primary inline-flex items-center gap-1"
                data-testid="join-resume-clear"
              >
                <X className="w-3 h-3" /> remove
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="join-resume-attach"
            >
              <FileUp className="w-3 h-3" /> Attach file
            </button>
          )}
        </Field>

        <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-5">
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary inline-flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="join-submit-btn"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <>Send it <ArrowRight className="w-4 h-4" /></>}
          </button>
          <p className="text-[10px] tracking-[0.18em] uppercase text-foreground/45 font-mono">
            Goes directly to hello@sisyphean.ai
          </p>
        </div>
      </form>

      <p className="text-[11px] text-foreground/45 mt-8 font-mono leading-relaxed inline-flex items-center gap-2">
        <User className="w-3 h-3" /> Already have an account?
        <Link to="/auth" className="text-primary underline underline-offset-4">Sign in</Link>
        to send your existing profile as a PDF.
      </p>
    </>
  );
}

function Field({ label, optional, required, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <span className="terminal-label">{label}</span>
        {required && <span className="text-[10px] tracking-[0.18em] uppercase text-primary font-mono">Required</span>}
        {optional && <span className="text-[10px] tracking-[0.18em] uppercase text-foreground/45 font-mono">Optional</span>}
      </div>
      {children}
      {hint && <p className="text-[11px] text-foreground/50 mt-1.5 font-mono">{hint}</p>}
    </label>
  );
}
